import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Check, MonitorPlay, Send } from "lucide-react";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/cn";
import { Ar, Num } from "@/deck/typography/Ar";
import { get, post, ApiError } from "@/lib/api";
import { CopyButton, PromptText } from "@/blocks/Prompt";
import { STRINGS } from "@/strings/strings.gen";

const MAX = 500;

/** How often a phone asks whether a poll has opened.
 *
 *  ⚠️ Polling, not a socket, and that is a decision rather than a shortcut: two
 *  hundred phones holding sockets against a single-worker process to send one
 *  message each is the one realistic way to exhaust it. A poll opening four
 *  seconds late is invisible in a room where someone is still reading the
 *  question aloud. */
const POLL_MS = 4000;

interface Quiz {
  id: string;
  prompt: string;
  options: string[];
  revealed: boolean;
  votes: number[];
  total: number;
  /** Only ever arrives WITH the reveal. Null means the deck marks no answer. */
  correct: number | null;
}

interface SharedPrompt {
  title?: string | null;
  text: string;
}

/** What the slide on the main screen offers this phone. */
interface SlideFeed {
  prompts: SharedPrompt[];
  /** A clip is playing on the main screen. Never played here. */
  media: { title: string } | null;
}

interface QuizFeed {
  active: boolean;
  quiz?: Quiz;
  yourVote?: number | null;
  slide?: SlideFeed | null;
}

/** How many past prompts a phone keeps for this visit. */
const KEEP_PROMPTS = 20;

const letter = (i: number) => String.fromCharCode(65 + i);

interface Status {
  code: string;
  title: string;
  status: string;
  allowQuestions: boolean;
}

/**
 * The only page a stranger ever sees.
 *
 * Deliberately the smallest possible surface: no nav, no links, no session, no
 * analytics. It is opened by scanning a code in a room, on venue wifi, by
 * someone with about fifteen seconds of patience.
 */
export default function AudienceScreen() {
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<Status | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const [feed, setFeed] = useState<QuizFeed>({ active: false });
  // Every prompt this phone has been offered, newest first. A prompt leaves the
  // feed the moment the presenter moves on, and someone halfway through
  // copying it must not lose it to a slide change.
  const [kept, setKept] = useState<SharedPrompt[]>([]);

  const pollQuiz = useCallback(() => {
    // Failures are silent on purpose. This runs every few seconds on venue
    // wifi; an error banner for a poll that will retry in four seconds would
    // just be a flashing light over the question someone is trying to read.
    get<QuizFeed>(`/a/${code}/quiz`, { raw: true })
      .then((f) => {
        setFeed(f);
        const fresh = f.slide?.prompts ?? [];
        if (fresh.length) {
          setKept((prev) => {
            const known = new Set(fresh.map((p) => p.text));
            return [...fresh, ...prev.filter((p) => !known.has(p.text))].slice(0, KEEP_PROMPTS);
          });
        }
      })
      .catch(() => {});
  }, [code]);

  useEffect(() => {
    pollQuiz();
    const id = window.setInterval(pollQuiz, POLL_MS);
    // A phone that was locked through the whole poll should not wait out
    // another full interval to discover it.
    const wake = () => { if (document.visibilityState === "visible") pollQuiz(); };
    document.addEventListener("visibilitychange", wake);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", wake);
    };
  }, [pollQuiz]);

  async function vote(option: number) {
    const quiz = feed.quiz;
    if (!quiz) return;
    // Optimistic: the tap must feel instant on a phone at the back of a room,
    // and the poll a moment later is what makes it true.
    setFeed((f) => ({ ...f, yourVote: option }));
    try {
      const r = await post<{ yourVote: number | null }>(
        `/a/${code}/vote`, { quizId: quiz.id, option }, { raw: true },
      );
      setFeed((f) => ({ ...f, yourVote: r.yourVote }));
    } catch {
      // Refused — the poll closed, or moved on. Let the next poll say so
      // rather than guessing here.
      setFeed((f) => ({ ...f, yourVote: null }));
      pollQuiz();
    }
  }

  useEffect(() => {
    document.title = "اطرح سؤالك";
    get<Status>(`/a/${code}`, { raw: true })
      .then(setStatus)
      .catch((e) => setLoadError(e instanceof ApiError ? e.message : "تعذّر الاتصال."));
  }, [code]);

  useEffect(() => {
    if (!sent) return;
    const id = window.setTimeout(() => setSent(false), 3000);
    return () => window.clearTimeout(id);
  }, [sent]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post(`/a/${code}/questions`, { text, name: name || null, role: role || null }, { raw: true });
      // Clear ONLY the question. Making someone retype who they are is the
      // fastest way to stop them asking a second one.
      setText("");
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "تعذّر الإرسال.");
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <Shell>
        <p role="alert" className="text-center text-text-muted"><Ar t={loadError} /></p>
      </Shell>
    );
  }
  if (!status) return <Shell><p className="text-center text-text-faint">…</p></Shell>;

  // ⚠️ The poll is rendered OUTSIDE the allowQuestions branch. Questions being
  // closed says nothing about whether a vote is open, and putting the poll
  // inside that branch meant closing questions silently closed voting too.
  const quizCard = feed.active && feed.quiz
    ? <QuizCard quiz={feed.quiz} yourVote={feed.yourVote ?? null} onVote={vote} />
    : null;
  // Outside the allowQuestions branch for the same reason as the poll.
  const now = feed.slide?.prompts ?? [];
  const nowTexts = new Set(now.map((p) => p.text));
  const earlier = kept.filter((p) => !nowTexts.has(p.text));
  const slideCards = (
    <>
      {feed.slide?.media && <WatchCard title={feed.slide.media.title} />}
      {now.length > 0 && <PromptList heading={STRINGS.audience.prompts.now} prompts={now} />}
      {earlier.length > 0 && <PromptList heading={STRINGS.audience.prompts.earlier} prompts={earlier} />}
    </>
  );

  if (!status.allowQuestions) {
    return (
      <Shell>
        <h1 className="text-center text-xl font-semibold"><Ar t={status.title} /></h1>
        {quizCard}
        {slideCards}
        <p className="mt-3 text-center text-text-muted">
          {status.status === "live" ? "الأسئلة مغلقة حالياً." : "انتهت هذه الجلسة."}
        </p>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-center text-xl font-semibold"><Ar t={status.title} /></h1>
      {quizCard}
      {slideCards}
      <p className="mt-1 text-center text-sm text-text-muted">اطرح سؤالك</p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm text-text-muted">سؤالك</span>
          <textarea
            autoFocus required rows={4} maxLength={MAX}
            value={text} onChange={(e) => setText(e.target.value)}
            className="w-full resize-none rounded-lg border border-border bg-surface-raised px-4 py-3 outline-none focus:border-accent"
          />
          <span className="mt-1 block text-end text-xs text-text-faint">
            {/* The cap is shown so the limit is visible, not a surprise. */}
            <Num>{text.length} / {MAX}</Num>
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-text-muted">اسمك (اختياري)</span>
            <input maxLength={60} value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:border-accent" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-text-muted">عملك (اختياري)</span>
            <input maxLength={120} value={role} onChange={(e) => setRole(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-raised px-3 py-2 text-sm outline-none focus:border-accent" />
          </label>
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-danger-surface px-4 py-2 text-sm text-danger-ink">
            <Ar t={error} />
          </p>
        )}

        <button type="submit" disabled={busy || !text.trim()}
          className={cn("inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 font-semibold text-accent-contrast",
            "disabled:opacity-50")}>
          <Send className="h-4 w-4 rtl:-scale-x-100" aria-hidden />
          {busy ? "…" : "أرسل"}
        </button>

        {/* An always-mounted slot: a confirmation that appears in the flow would
            shove the submit button down under the reader's thumb as they read it. */}
        <div className="h-12">
          {sent && (
            <p className="animate-slide-in rounded-lg bg-success-surface px-4 py-2 text-center text-sm text-success-ink">
              وصل سؤالك. شكراً لك.
            </p>
          )}
        </div>
      </form>
    </Shell>
  );
}

/**
 * The poll, on a phone in the room.
 *
 * Big targets and nothing else: this is tapped one-handed, at arm's length,
 * by someone who is also listening to a person talk. There is no "confirm"
 * step, because one tap is the whole interaction and a second one would be the
 * most common way to miss the vote entirely.
 */
function QuizCard({ quiz, yourVote, onVote }: {
  quiz: Quiz;
  yourVote: number | null;
  onVote: (option: number) => void;
}) {
  const voted = yourVote !== null;
  const locked = voted || quiz.revealed;
  const share = (i: number) =>
    quiz.total > 0 ? Math.round((quiz.votes[i] ?? 0) * 100 / quiz.total) : 0;

  return (
    <section className="mt-5 rounded-xl border border-accent-border bg-accent-surface p-4">
      <p className="text-base font-semibold leading-relaxed text-text"><Ar t={quiz.prompt} /></p>

      <div className="mt-4 space-y-2">
        {quiz.options.map((option, i) => {
          const mine = yourVote === i;
          const right = quiz.revealed && quiz.correct === i;
          return (
            <button
              key={i}
              type="button"
              disabled={locked}
              onClick={() => onVote(i)}
              className={cn(
                "relative w-full overflow-hidden rounded-lg px-4 py-3 text-start transition-all",
                // The same three signals as the projector — fill, ring weight,
                // scale — so a phone in the third row and the screen at the
                // front are telling the reader the same thing the same way.
                right ? "scale-[1.02] border-4 border-quiz-correct-edge bg-quiz-correct"
                  : mine ? "border-2 border-accent-border bg-accent-surface"
                  : "border border-border bg-quiz-track",
                // Not `disabled:opacity-50`: after the reveal these rows carry
                // the result, and fading them makes the answer hard to read.
                locked && !mine && !right && "opacity-70",
              )}
            >
              {quiz.revealed && (
                <span
                  aria-hidden
                  className={cn("absolute inset-y-0 start-0 transition-[width] duration-700",
                    right ? "bg-quiz-correct-bar" : "bg-quiz-bar")}
                  style={{ width: `${share(i)}%` }}
                />
              )}
              <span className="relative flex items-center gap-3">
                <span dir="ltr" className={cn(
                  "grid h-8 w-8 shrink-0 place-items-center rounded-md text-sm font-bold",
                  right ? "bg-quiz-correct-ink text-quiz-correct"
                    : mine ? "bg-accent text-accent-contrast"
                    : "bg-surface-hover text-text-muted",
                )}>
                  {right ? <Check className="h-4 w-4" /> : letter(i)}
                </span>
                <span className={cn("flex-1", right ? "font-semibold text-quiz-correct-ink" : "text-text")}>
                  <Ar t={option} />
                </span>
                {quiz.revealed && (
                  <span className={cn("shrink-0 text-sm font-bold tabular-nums", right ? "text-quiz-correct-ink" : "text-text-muted")} dir="ltr">
                    <Num>{share(i)}</Num>%
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-center text-sm">
        {quiz.revealed ? (
          quiz.correct === null ? (
            <span className="text-text-muted"><Ar t="ظهرت النتائج على الشاشة." /></span>
          ) : yourVote === quiz.correct ? (
            <span className="text-success-ink"><Ar t="إجابتك صحيحة ✓" /></span>
          ) : (
            <span className="text-text-muted">
              <Ar t={`الإجابة الصحيحة: ${letter(quiz.correct)}`} />
            </span>
          )
        ) : voted ? (
          <span className="text-success-ink"><Ar t="تم التصويت ✓" /></span>
        ) : (
          <span className="text-text-faint"><Ar t="اختر إجابة واحدة" /></span>
        )}
      </p>
    </section>
  );
}

/**
 * A clip is playing on the main screen. Deliberately NOT a player: twenty
 * phones pulling the same video over venue wifi degrade everyone's playback,
 * and a phone that plays it is out of step with the room anyway.
 */
function WatchCard({ title }: { title: string }) {
  return (
    <section className="mt-5 flex items-center gap-4 rounded-xl border border-accent-border bg-accent-surface p-4">
      <MonitorPlay aria-hidden className="h-8 w-8 shrink-0 text-accent-ink" />
      <div className="min-w-0">
        <p className="font-semibold text-text"><Ar t={STRINGS.audience.media.title} /></p>
        <p className="mt-0.5 text-sm text-text-muted"><Ar t={STRINGS.audience.media.hint} /></p>
        <p className="mt-1 truncate text-sm text-text-faint"><Ar t={title} /></p>
      </div>
    </section>
  );
}

/**
 * Prompts to copy. The point of the page when a slide carries one: a prompt
 * that has to be retyped from a projector is worth nothing.
 */
function PromptList({ heading, prompts }: { heading: string; prompts: SharedPrompt[] }) {
  return (
    <section className="mt-5 space-y-3">
      <h2 className="text-sm font-semibold text-text-muted"><Ar t={heading} /></h2>
      {prompts.map((p) => (
        <article key={p.text} className="overflow-hidden rounded-xl border border-border bg-surface-raised">
          {p.title && (
            <p className="border-b border-border px-4 py-2 text-sm font-semibold text-text"><Ar t={p.title} /></p>
          )}
          <PromptText text={p.text} className="max-h-72 overflow-y-auto px-4 py-3 text-base leading-relaxed text-text" />
          <div className="border-t border-border p-2">
            {/* Full width and tall: tapped one-handed, at arm's length. */}
            <CopyButton text={p.text} className="w-full py-3 text-base" />
          </div>
        </article>
      ))}
    </section>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div dir="rtl" className="min-h-[100dvh] bg-bg px-5 py-10 font-deck text-text">
      <div className="mx-auto w-full max-w-md">{children}</div>
    </div>
  );
}
